import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack, useTheme } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import { type Viewport } from '@/designs-dev/device/viewport';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import DesktopShell from '@/karehero-app/shell/DesktopShell';
import Flow, { type Frame } from '@/karehero-app/tell-us-once/intake/Flow';
import {
  INTAKE_CHAT_FRAMES_V20,
  INTAKE_CHAT_SCENES,
  INTAKE_JOURNEYS,
  INTAKE_SECTIONS,
  intakeSceneForFrame,
  intakeScenes,
  journeyOfSection,
  type IntakeJourney,
} from '@/karehero-app/tell-us-once/intake/Flow.scenes';
import { type Scene } from '@/designs-dev/scenes';

// The scenes that are the chat itself. Read off the question map rather than
// listed again, so adding a question cannot leave this behind.
const CHAT_SCENES = new Set(Object.values(INTAKE_CHAT_SCENES));

// The journey pill is a StateSelector of its own, so it looks and steps like
// the screen pill beside it. Each journey is a "scene" that opens on that
// journey's first screen.
const JOURNEY_SCENES: Scene<string>[] = INTAKE_JOURNEYS.map((j) => ({
  id: j.id,
  label: j.label,
  kind: 'happy',
  section: 'journey',
  initial:
    intakeScenes.find((s) => journeyOfSection(s.section) === j.id)?.id ??
    intakeScenes[0].id,
}));
const JOURNEY_SECTIONS = [{ id: 'journey', label: 'Version' }];

// Where the prototype opens with no ?scene: the latest version's first screen,
// Onboarding 4.1, so the prototype always shows the most recent work.
const OPENING_SCENE =
  intakeScenes.find((s) => journeyOfSection(s.section) === 'onboarding-2-1')
    ?.id ?? intakeScenes[0].id;

const firstSceneOf = (journey: IntakeJourney) =>
  intakeScenes.find((s) => journeyOfSection(s.section) === journey)?.id ??
  OPENING_SCENE;

type StageProps = {
  /**
   * One journey only, opened in place on that journey's flows page rather than
   * routed to. No journey pill, and the URL is the page's, not this: the scene
   * is not written to it or read from it.
   */
  journey?: IntakeJourney;
};

const Stage = ({ journey: only }: StageProps) => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sceneId, setSceneId] = useState(() =>
    only ? firstSceneOf(only) : (searchParams.get('scene') ?? OPENING_SCENE),
  );

  // Clamp to a scene that exists, so a stale ?scene never breaks the page.
  const scene =
    intakeScenes.find((s) => s.id === sceneId) ??
    intakeScenes.find((s) => s.id === OPENING_SCENE) ??
    intakeScenes[0];

  // Two different things, so two states. `sceneId` is where the flow was
  // mounted — picking it remounts, which is what makes a screen start clean.
  // `shown` is the screen the flow has since been clicked through to, which is
  // what the pill and the URL should say. Keeping them apart is the point:
  // driving the mount off every step would remount on every tap and throw away
  // the answers the reader had just typed.
  const [shown, setShown] = useState<string>(scene.id);

  // Two ways the screen changes, so two things to follow. A frame is a screen of
  // its own; the chat is a dozen questions inside one frame, and answering one
  // moves the screen without moving the frame.
  //
  // Matched on what a scene opens rather than on its id: the two are the same
  // for every screen but the fork, which is addressed as ?scene=chat-v2 and
  // opens the chat partway through.
  //
  // Frames and questions the list has no entry for (the opening questions on
  // their own screens, a chat with its first answer pre-chosen) leave the pill
  // where it was rather than blanking it.
  // The journey the flow was opened in. A frame is shared by both onboardings,
  // so following one has to stay in the journey it was opened from.
  const mounted = journeyOfSection(scene.section);

  const follow = (frame: Frame) => {
    // Only within the version it was opened in. The calculator is opened from
    // Home, but it is separate work and not on the version pill, so following
    // into it leaves the pills where they were.
    const at = intakeSceneForFrame(frame, mounted);
    if (at) setShown(at);
  };

  // A question's screen in the journey it was opened in. A journey without
  // that screen leaves the pill where it was rather than jumping journeys.
  const followChat = (question: string) => {
    // 4.0's questions are its own, so they have their own map. 4.1 shares it:
    // the same questions, with its own new turns left unmapped.
    if (mounted === 'onboarding-2-0' || mounted === 'onboarding-2-1') {
      const frame = INTAKE_CHAT_FRAMES_V20[question];
      const own =
        frame && `${mounted === 'onboarding-2-1' ? 'v21' : 'v20'}-${frame}`;
      if (own && intakeScenes.some((s) => s.id === own)) setShown(own);
      return;
    }
    const base = INTAKE_CHAT_SCENES[question];
    if (!base) return;
    if (mounted === 'onboarding') return setShown(base);
    const prefix = mounted === 'onboarding-1-1' ? 'v11' : 'v20';
    const own = `${prefix}-${intakeScenes.find((s) => s.id === base)?.initial}`;
    if (intakeScenes.some((s) => s.id === own)) setShown(own);
  };

  // The open screen goes in the URL, so a screen can be linked to rather than
  // described — and clicking through the prototype keeps that address current.
  useEffect(() => {
    if (only) return;
    if (searchParams.get('scene') !== shown) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', shown);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  // Which journey the open screen belongs to. The screen pill only lists that
  // journey's screens, so the two read as "which part, then which screen".
  const journey = journeyOfSection(
    intakeScenes.find((s) => s.id === shown)?.section,
  );
  const journeyScenes = intakeScenes.filter(
    (s) => journeyOfSection(s.section) === journey,
  );
  const journeySections = INTAKE_SECTIONS.filter(
    (s) => journeyOfSection(s.id) === journey,
  );

  // Phone mockup, full-bleed fill, or pinned to a breakpoint width.
  const [viewport, setViewport] = useState<Viewport>('frame');
  const framed = viewport === 'frame';
  // At md and up the member app is not a stretched phone: it follows the desktop
  // pattern the chat on a desktop set, so the screen gets the bar and the
  // centred column rather than one 375px design spread across a monitor. Read
  // off the theme so it moves with the breakpoints. 'fill' is left alone — that
  // one is for opening designs.dev on a real phone.
  const theme = useTheme();
  const desktop =
    typeof viewport === 'number' && viewport >= theme.breakpoints.values.md;
  // Screen element for drill mode when unframed (PhoneFrame provides its own
  // when framed).
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  const exitDrillMode = useCallback(() => {
    setActive(false);
    setCurrent(null);
    setHovered(null);
  }, [setActive, setCurrent, setHovered]);

  // Esc is staged: a first press clears a selected layer, a second leaves drill
  // mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        exitDrillMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, exitDrillMode, setCurrent, setHovered]);

  // The chat's way to replay the bottom bar's change, when the open screen is
  // one of the chat's. In a ref rather than state: it is a handle to call, not
  // something the toolbar renders from, and putting it in state re-rendered the
  // stage every time a scene mounted.
  const replay = useRef<(() => void) | null>(null);

  // Switching screen clears any pinned component, so a ?select doesn't linger
  // onto the new (remounted) scene's nodes.
  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
    setShown(id);
  };

  // A bare link to the prototype (the menu's, or one with the scene taken off)
  // always opens on the latest version, even when the stage is already open on
  // an older screen. A link that names a ?scene still opens on that scene.
  const bare = !only && searchParams.get('scene') === null;
  useEffect(() => {
    if (bare && shown !== OPENING_SCENE) pickScene(OPENING_SCENE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bare]);

  // The chat flow only. The basic flow is on the flows page to be read against
  // this one, not to be clicked through.
  //
  // Keyed on the scene, so picking one starts it clean rather than carrying the
  // last screen's answers into it.
  const flow = (
    <Flow
      key={scene.id}
      initial={scene.initial}
      option={2}
      version={
        // 4.0 is the dev build; 4.1 is it with 4.1's changes.
        mounted === 'onboarding-2-1'
          ? '2.1'
          : mounted === 'onboarding-2-0'
            ? '2.0-dev'
            : mounted === 'onboarding-1-1'
              ? '1.1'
              : '1.0'
      }
      onFrameChange={follow}
      onChatQuestion={followChat}
      onReplay={(fn) => {
        replay.current = fn;
      }}
    />
  );

  const screen = (
    <>
      {desktop ? <DesktopShell>{flow}</DesktopShell> : flow}
      <InspectOverlay />
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        {/* The height is the point: this screen has no chrome strip above it, so
          nothing else gives the page a height to centre in. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: framed ? 'center' : 'stretch',
            justifyContent: 'center',
            p: 3,
            // Room for the floating controls under the screen. Framed, the same
            // 96px the phone reserves below itself, so it centres in the space
            // above the controls rather than in the whole stage.
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              width: framed ? 'auto' : viewport === 'fill' ? '100%' : viewport,
              flex: viewport === 'fill' ? 1 : undefined,
              minHeight: 0,
            }}
            spacing={2}
          >
            {framed ? (
              <PhoneFrame inspecting={active}>{screen}</PhoneFrame>
            ) : (
              /* Out of the frame: fills the stage, or lays out at a fixed
               breakpoint width — e.g. opening designs.dev on a real phone for
               user testing. */
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: `${radius.md}px`,
                  border: 1,
                  borderColor: 'custom.neutral.200',
                  bgcolor: 'background.default',
                }}
                ref={(el: HTMLElement | null) => setFullEl(el)}
              >
                <PhoneScreenContext.Provider value={fullEl}>
                  {screen}
                </PhoneScreenContext.Provider>
              </Box>
            )}
          </Stack>
        </Box>

        {/* The controls, floating at the foot of the stage: how you are
            looking at the design, the version, then which screen it is
            showing. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          {!only && (
            <StateSelector
              scenes={JOURNEY_SCENES}
              sceneId={journey}
              onSceneChange={(id) => {
                const to = JOURNEY_SCENES.find((j) => j.id === id);
                if (to && id !== journey) pickScene(to.initial);
              }}
              sections={JOURNEY_SECTIONS}
            />
          )}
          <StateSelector
            scenes={journeyScenes}
            sceneId={shown}
            onSceneChange={pickScene}
            sections={journeySections}
          />
          {/* Loft's own chrome, so it sits with the other two pills rather
            than inside the design. Drawn in the transcript it read as
            something the app offers, and the orb covered it. */}
          {CHAT_SCENES.has(scene.id) && (
            <Button
              variant='secondary'
              size='sm'
              ariaLabel='Play the bottom bar transition'
              onPress={() => replay.current?.()}
            >
              Play transition
            </Button>
          )}
        </StageBar>
      </Box>

      {/* Docked full-height panels beside the stage, like the other stages.
          Both render null while drill mode is off. */}
      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const IntakeProfileReview = (props: StageProps) => (
  <InspectProvider>
    <Stage {...props} />
  </InspectProvider>
);

export default IntakeProfileReview;
